import React, { memo, ReactElement, ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { HelpOutline } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid2,
  Radio,
  RadioGroup,
} from '@mui/material';

import { ControlledEmailField } from '../EmailField';
import { ControlledInnField } from '../InnField';
import { DatePicker } from '../lib/DatePicker';
import { HelperText } from '../lib/HelperText';
import { PhoneField } from '../lib/PhoneField';
import { TextField } from '../lib/TextField';
import { StyledFormControl } from '../lib/TextField/styles';
import { ControlledPasswordField } from '../PasswordField';
import { ControlledSelect } from '../Select';
import { Switch } from '../Switch';
import { ControlledTextField } from '../TextField';
import { CustomTypography } from '../Typography';

import { FormFieldValues } from './const';
import { IFormGeneratorProps, TFormFieldTypes } from './FormGenerator.types';
import { useDependentFieldsRevalidation } from './hooks';

const getFormGeneratorFieldTestId = (type: TFormFieldTypes, name: string) =>
  `${type}_${name}`;

export const FormGenerator = memo(function FormGeneratorComponent(
  props: IFormGeneratorProps
) {
  const {
    formScheme = [],
    viewable,
    className,
    disabled,
    allowColumns,
  } = props;
  const { control, watch, formState } = useFormContext();

  useDependentFieldsRevalidation(formScheme);

  return (
    <Grid2
      container={!allowColumns}
      data-qa="form-generator"
      className={className}
      sx={{ gap: 2 }}
    >
      {formScheme.map(
        ({
          name,
          helpText,
          label,
          required,
          hideRequiredStyle,
          type = FormFieldValues.Default,
          dependencies,
          shouldShow,
          inputProps,
          component,
          ...other
        }) => {
          const key = Array.isArray(name) ? name.join() : name;

          const commonProps = {
            key,
            name,
            label,
            'data-qa': getFormGeneratorFieldTestId(type, key),
            disabled,
            ...other,
          };

          let content: ReactNode = null;

          if (!viewable || viewable.isEdit) {
            switch (type) {
              case FormFieldValues.Divider: {
                content = <Divider {...commonProps} />;
                break;
              }

              case FormFieldValues.Title: {
                content = (
                  <Grid2
                    component="div"
                    key={key}
                    container
                    alignItems="center"
                  >
                    {required && (
                      <CustomTypography
                        color="error"
                        component="span"
                        sx={{ mr: 1 }}
                      >
                        *
                      </CustomTypography>
                    )}
                    <CustomTypography
                      {...commonProps}
                      variant="h5"
                      sx={{ mb: 2 }}
                    >
                      {label}
                    </CustomTypography>
                    {helpText && (
                      <HelpOutline fontSize="small" sx={{ ml: 1 }} />
                    )}
                  </Grid2>
                );
                break;
              }

              case FormFieldValues.Description: {
                content = (
                  <CustomTypography
                    color="textSecondary"
                    sx={{ mb: 2 }}
                    {...commonProps}
                  >
                    {label}
                  </CustomTypography>
                );
                break;
              }

              case FormFieldValues.Raw: {
                content = component;
                break;
              }

              case FormFieldValues.Custom: {
                if (component) {
                  content = (
                    <Grid2 component="div" key={key}>
                      <Controller
                        name={name}
                        control={control}
                        render={({ field }) =>
                          React.cloneElement(component as ReactElement, {
                            ...field,
                            ...inputProps,
                          })
                        }
                      />
                    </Grid2>
                  );
                }
                break;
              }

              case FormFieldValues.Error: {
                content = (
                  <Grid2 component="div" key={key}>
                    <Box sx={{ height: '24px' }} />
                  </Grid2>
                );
                break;
              }

              case FormFieldValues.Date: {
                content = (
                  <Grid2 component="div" key={key}>
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <DatePicker {...field} {...inputProps} label={label} />
                      )}
                    />
                  </Grid2>
                );
                break;
              }

              case FormFieldValues.Textarea: {
                content = (
                  <Grid2 component="div" key={key}>
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          multiline
                          rows={4}
                          fullWidth
                          {...inputProps}
                        />
                      )}
                    />
                  </Grid2>
                );
                break;
              }

              case FormFieldValues.Password: {
                content = (
                  <ControlledPasswordField
                    key={key}
                    name={name}
                    control={control}
                    label={label as string}
                    helperText={formState.errors[name]?.message as string}
                    error={!!formState.errors[name]}
                    {...inputProps}
                  />
                );
                break;
              }

              case FormFieldValues.Mask: {
                content = (
                  <Grid2 component="div" key={key}>
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} fullWidth {...inputProps} />
                      )}
                    />
                  </Grid2>
                );
                break;
              }
              case FormFieldValues.Email: {
                content = (
                  <ControlledEmailField
                    key={key}
                    name={name}
                    control={control}
                    {...inputProps}
                  />
                );
                break;
              }

              case FormFieldValues.Checker: {
                content = (
                  <Grid2 component="div" key={key}>
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <StyledFormControl error={!!formState.errors[name]}>
                          <FormControlLabel
                            sx={{ marginRight: 0 }}
                            control={
                              <Checkbox {...field} checked={field.value} />
                            }
                            label={label}
                          />
                          <HelperText>
                            {formState.errors[name]?.message as string}
                          </HelperText>
                        </StyledFormControl>
                      )}
                    />
                  </Grid2>
                );
                break;
              }

              case FormFieldValues.Boolean: {
                content = (
                  <Grid2 component="div" key={key}>
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <RadioGroup {...field} row sx={{ gap: 4 }}>
                          <FormControlLabel
                            value
                            control={<Radio />}
                            label="Да"
                          />
                          <FormControlLabel
                            value={false}
                            control={<Radio />}
                            label="Нет"
                          />
                        </RadioGroup>
                      )}
                    />
                  </Grid2>
                );
                break;
              }

              case FormFieldValues.Switch: {
                content = (
                  <Grid2 component="div" key={key}>
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} />}
                          label={label}
                        />
                      )}
                    />
                  </Grid2>
                );
                break;
              }

              case FormFieldValues.Select: {
                content = (
                  <Grid2 component="div" key={key}>
                    <ControlledSelect
                      name={name}
                      control={control}
                      label={label}
                      {...inputProps}
                    />
                  </Grid2>
                );
                break;
              }

              case FormFieldValues.Inn: {
                content = (
                  <ControlledInnField
                    key={key}
                    name={name}
                    control={control}
                    label={label}
                    {...inputProps}
                  />
                );
                break;
              }

              case FormFieldValues.Group: {
                content = (
                  <Grid2
                    container
                    key={key}
                    spacing={2}
                    sx={{ marginTop: 2, alignItems: 'center' }}
                    direction={other.direction || 'row'}
                  >
                    {other?.fields?.map((field) => (
                      <Grid2 size={6} key={field.name}>
                        <FormGenerator
                          formScheme={[field]}
                          viewable={viewable}
                          disabled={disabled}
                        />
                      </Grid2>
                    ))}
                  </Grid2>
                );
                break;
              }

              case FormFieldValues.Phone: {
                content = (
                  <Controller
                    key={key}
                    name={name}
                    control={control}
                    render={({ field }) => (
                      <PhoneField
                        {...field}
                        {...inputProps}
                        helperText={formState.errors[name]?.message as string}
                        error={!!formState.errors[name]}
                      />
                    )}
                  />
                );
                break;
              }

              default: {
                content = (
                  <ControlledTextField
                    key={key}
                    name={name}
                    control={control}
                    label={label}
                    error={!!formState.errors[name]}
                    helperText={formState.errors[name]?.message as string}
                    required={required}
                    fullWidth
                    {...inputProps}
                  />
                );
              }
            }
          }

          if (dependencies && shouldShow) {
            const currentValues = watch(dependencies);
            if (!shouldShow(currentValues)) {
              return null;
            }
          }

          return content;
        }
      )}
    </Grid2>
  );
});
